import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Switch} from '../../buttons/Swtich';
import {X} from '../../icons';
import {isOk} from '../../../utils/apiResponse';
import {AlertRulesApi} from '../../../api/alertRulesApi';
import {RuleEffects} from './RuleEffects';
import {ThresholdFields} from '../shared/ThresholdFields';
import {systemRuleTitle} from '../ruleText';
import {ALERTS_NEW, lineAlertsConfigPath} from '../alertsCenterPaths';
import {errorFromResponse} from '../rulesModel';
import {
    howKey,
    initialThresholdValues,
    isColorOffSilentRule,
    isPaintOnlyRule,
    savedThresholdsPatch,
    systemSavePayload,
    thresholdFields,
    thresholdHintKeys,
    thresholdLabelKey,
    thresholdsChanged,
    validateThresholds,
} from '../systemRulesModel';

// Mientras el panel está abierto, el chat de soporte y la pestaña de feedback se ocultan (quedaban por
// encima del panel): el mismo mecanismo que `LineEditDrawer` y el cajón de Cuentas
// (C-ac-fe-centro-15). La clase se saca al desmontar.
export const PANEL_BODY_CLASS = 'alc-drawer-open';

// Lo que se puede enfocar dentro del panel, para la trampa de foco (patrón de `AccountDrawer`).
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
    + 'textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * El panel de una regla de Dashbo (las sensibilidades de siempre), al costado de «Mis reglas». Es la
 * pantalla de alertas de la agencia en la 2.1, una regla a la vez (plan interino «sleepy-emerson»,
 * puntos 3-9, absorbido por el Centro):
 *
 * - Los umbrales según la regla: Malo/Crítico, Advertencia/Crítico en días, el 1.º y 2.º aviso de
 *   inversión, o uno solo en Hoy. La validación va en la fila: Malo antes que Crítico (en días, al
 *   revés) y vacío no; el backend guarda lo que le llegue.
 * - Guardar es el MISMO `POST agency/alerts-configuration` de la pantalla clásica, parcial: sólo esta
 *   regla. Con un `toast`, nunca `alert()`.
 * - El interruptor no espera a «Guardar»: apaga color y aviso juntos en el acto, igual que el de la
 *   fila. Dos interruptores de la misma cosa con dos comportamientos se leerían como dos cosas.
 *
 * - El interruptor y «Guardar umbrales» no corren a la vez: mientras viaja uno, el otro espera. Si no,
 *   un interruptor tocado con el POST de umbrales en vuelo podía quedar pisado (C-ac-fe-centro-12).
 *
 * Va por portal a `body` (el marco de la 2.1 crea contextos de apilamiento) y se cierra con Escape,
 * con la X y con un clic afuera. Es modal: el foco queda adentro (Tab da la vuelta) y vuelve a quien
 * lo abrió.
 *
 * `readOnly`: quien mira no puede editar las alertas de la agencia (`can_edit` en false): se lee todo
 * y no se cambia nada.
 */
export function SystemRulePanel({rule, effects, busy, readOnly = false, onToggle, onSaved, onClose}) {
    const {t, i18n} = useTranslation();
    const locale = i18n.language;
    const [values, setValues] = useState(() => initialThresholdValues(rule, {locale}));
    const [saving, setSaving] = useState(false);
    const panelRef = useRef(null);
    const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
    const {title, subtitle} = systemRuleTitle(rule, t);
    const fields = useMemo(() => thresholdFields(rule, {locale}), [rule, locale]);
    const errors = validateThresholds(rule, values, {locale});
    const hasErrors = Object.keys(errors).length > 0;
    const changed = thresholdsChanged(rule, values, {locale});
    const active = rule.is_active !== false;

    // Otra regla en el mismo panel (un segundo deep link): los valores son los de ESA regla.
    const ruleId = rule.id;
    useEffect(() => {
        setValues(initialThresholdValues(rule, {locale}));
        // Sólo al cambiar de regla: un guardado que actualiza la misma regla no pisa lo tipeado.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ruleId]);

    // Una sola vez por apertura: el foco entra al panel y, al cerrar, vuelve a quien lo abrió. El
    // `onClose` va por ref para que un padre que lo recrea en cada render no re-dispare esto.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        const opener = openerRef.current;
        if (panelRef.current) panelRef.current.focus();
        document.body.classList.add(PANEL_BODY_CLASS);
        const onKey = (event) => {
            if (event.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.classList.remove(PANEL_BODY_CLASS);
            if (opener && opener.focus) opener.focus();
        };
    }, []);

    // `aria-modal` sin trampa de foco dejaba salir con Tab a la pantalla de atrás (C-ac-fe-centro-16).
    const trapTab = (event) => {
        if (event.key !== 'Tab' || !panelRef.current) return;
        const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const setValue = (level, value) => setValues((current) => ({...current, [level]: value}));

    const save = (event) => {
        event.preventDefault();
        if (hasErrors || !changed || saving || busy || readOnly) return;
        setSaving(true);
        AlertRulesApi.saveSystemRules(systemSavePayload(rule, values, active, {locale})).then((response) => {
            setSaving(false);
            if (!isOk(response)) {
                toast.error(t(errorFromResponse(response, {fallbackKey: 'alerts_center.rules.save_error'}).key));
                return;
            }
            toast.success(t('alerts_center.rules.saved_toast'));
            onSaved(savedThresholdsPatch(rule, values, {locale}));
        });
    };

    const titleId = 'alc-drawer-title';
    const lines = rule.lines_with_own_config || [];

    return createPortal(
        <div className="alc-drawer-layer">
            <div className="alc-drawer-backdrop" onMouseDown={onClose}/>
            <aside className="alc-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId}
                   ref={panelRef} tabIndex={-1} data-testid="alc-system-panel" onKeyDown={trapTab}>
                <header className="alc-drawer__head">
                    <div>
                        <span className="alc-drawer__eyebrow">{t('alerts_center.rules.system_panel_title')}</span>
                        <h2 id={titleId} className="alc-drawer__title">{title}</h2>
                        {subtitle && <p className="alc-drawer__subtitle">{subtitle}</p>}
                    </div>
                    <button type="button" className="alc-drawer__close" aria-label={t('alerts_center.close')}
                            onClick={onClose}>
                        <X size={18} weight="bold"/>
                    </button>
                </header>

                <div className="alc-drawer__body">
                    <div className="alc-drawer__toggle">
                        <div className="alc-drawer__toggle-text">
                            <span className="alc-drawer__toggle-label" id="alc-drawer-switch">
                                {active ? t('alerts_center.rules.state_on') : t('alerts_center.rules.state_off')}
                            </span>
                            {/* Una regla que no avisa no tiene aviso que apagar: decirlo, y ofrecer el camino a
                                quien quiera uno (fede, 08/10). Si además le sacaron el color desde el semáforo
                                de la columna, tampoco hay color que quitar (C-ac-fe-centro-26). */}
                            {isPaintOnlyRule(rule) || isColorOffSilentRule(rule) ? (
                                <span className="alc-drawer__toggle-hint" data-testid="alc-switch-hint-paint-only">
                                    {t(isPaintOnlyRule(rule) ? 'alerts_center.rules.switch_hint_paint_only'
                                        : 'alerts_center.rules.switch_hint_color_off')}
                                    {!readOnly && (
                                        <>
                                            {' '}
                                            <Link to={ALERTS_NEW} className="alc-link">{t('alerts_center.cta_new')}</Link>
                                        </>
                                    )}
                                </span>
                            ) : (
                                <span className="alc-drawer__toggle-hint">{t('alerts_center.rules.switch_hint')}</span>
                            )}
                        </div>
                        <Switch name="alc-drawer-switch" className="alc-switch" checked={active}
                                disabled={busy || saving || readOnly}
                                aria-labelledby="alc-drawer-switch"
                                onChange={(event) => onToggle(rule, event.target.checked)}/>
                    </div>

                    <RuleEffects effects={effects}/>

                    <form className="alc-drawer__section" onSubmit={save} noValidate>
                        <h3 className="alc-drawer__section-title">{t('alerts_center.rules.thresholds_title')}</h3>
                        <ThresholdFields fields={fields} values={values} errors={errors} readOnly={readOnly}
                                         labelFor={(level) => t(thresholdLabelKey(rule, level))} onChange={setValue}/>
                        <p className="alc-hint" data-testid="alc-threshold-hint">
                            {thresholdHintKeys(rule).map((key) => t(key)).join(' ')}
                        </p>
                        {readOnly && <p className="alc-notice -warn" role="note">{t('alerts_center.no_edit_permission')}</p>}
                        <div className="alc-drawer__actions">
                            <button type="submit" className="alc-btn -primary"
                                    disabled={!changed || hasErrors || saving || busy || readOnly}>
                                {saving ? t('alerts_center.saving') : t('alerts_center.rules.save_thresholds')}
                            </button>
                            {changed && (
                                <button type="button" className="alc-btn -ghost" disabled={saving}
                                        onClick={() => setValues(initialThresholdValues(rule, {locale}))}>
                                    {t('alerts_center.rules.discard')}
                                </button>
                            )}
                        </div>
                    </form>

                    <details className="alc-how">
                        <summary>{t('alerts_center.rules.system_panel_how')}</summary>
                        <p>{t(howKey(rule))}</p>
                    </details>

                    {lines.length > 0 && (
                        <section className="alc-drawer__section">
                            <h3 className="alc-drawer__section-title">
                                {t('alerts_center.rules.own_config', {count: lines.length})}
                            </h3>
                            <p className="alc-hint">{t('alerts_center.rules.own_config_hint')}</p>
                            <ul className="alc-own__list">
                                {lines.map((line) => (
                                    <li key={line.business_unit_id}>
                                        <Link to={lineAlertsConfigPath(line.business_unit_id)} className="alc-link">
                                            {line.client_name ? `${line.client_name} · ` : ''}{line.business_unit_name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>
            </aside>
        </div>,
        document.body,
    );
}
